import React from "react";
import {
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Box } from "@mui/system";
import UserAvatar from "../../common/UserAvatar";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  StyledSelect,
  StyledTextField,
} from "../../customComponents/styled/styledInputs";
import CustomFileInput from "../../customComponents/CustomFileInput";
import {
  FilledButton,
  StyledLoadingButton,
} from "../../customComponents/styled/styledButtons";
import ChangePasswordDialog from "./ChangePasswordDialog";
import {
  useGetProfileQuery,
  useGetUserProfileQuery,
  useUpdateProfileMutation,
  useUpdateUserProfileMutation,
} from "./dashboardApiSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser, setUser } from "../auth/authSlice";
import { useUploadFileMutation } from "../../../app/utilsApiSlice";
import { format, parseISO } from "date-fns";
import toast from "../../../helpers/toast";
import CloseIcon from "@mui/icons-material/Close";
import {
  phoneYupSchema,
  formatPhoneNumberInput,
  PHONE_INPUT_PROPS,
} from "../../../helpers/phoneUtils";

const validationSchema = yup.object({
  title: yup.string().required("Required"),
  first_name: yup.string().required("Required"),
  last_name: yup.string().required("Required"),
  gender: yup.string().required("Required"),
  dob: yup.string(),
  phone: phoneYupSchema,
  email: yup.string().email("Enter a valid email").required("Required"),
  address: yup.string().required("Required"),
});

function CvPreviewModal({ url, open, onClose }) {
  const [iframeLoading, setIframeLoading] = React.useState(true);
  const googleDocsUrl = url
    ? `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`
    : null;

  React.useEffect(() => {
    if (open) setIframeLoading(true);
  }, [open, url]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: "90vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        CV Preview
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ p: 0, overflow: "hidden", position: "relative", bgcolor: "#fff" }}
      >
        {iframeLoading && googleDocsUrl && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.85)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {googleDocsUrl && (
          <iframe
            key={url}
            src={googleDocsUrl}
            title="CV Preview"
            width="100%"
            height="100%"
            style={{ border: "none", backgroundColor: "#fff" }}
            onLoad={() => setIframeLoading(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditProfile() {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const isTeacher = user?.roles?.some((r) => r?.role?.name === "teacher");
  const [openChangePassword, setOpenChangePassword] = React.useState(false);
  const [openCvPreview, setOpenCvPreview] = React.useState(false);
  const [fileLoading, setFileLoading] = React.useState({
    photo: false,
    signature: false,
    resume: false,
  });

  const { data: teacherInfo, isLoading: getTeacherProfileLoading } =
    useGetProfileQuery(undefined, { skip: !isTeacher });
  const { data: userInfo, isLoading: getUserProfileLoading } =
    useGetUserProfileQuery(undefined, { skip: isTeacher });
  const profileData = isTeacher ? teacherInfo : userInfo;
  const getProfileLoading = isTeacher
    ? getTeacherProfileLoading
    : getUserProfileLoading;

  const [updateTeacherProfile, { isLoading: updateTeacherProfileLoading }] =
    useUpdateProfileMutation();
  const [updateUserProfile, { isLoading: updateUserProfileLoading }] =
    useUpdateUserProfileMutation();
  const updateProfile = isTeacher ? updateTeacherProfile : updateUserProfile;
  const updateProfileLoading = isTeacher
    ? updateTeacherProfileLoading
    : updateUserProfileLoading;

  const [uploadFile] = useUploadFileMutation();

  const formik = useFormik({
    initialValues: {
      title: profileData?.title || "Mr.",
      first_name: profileData?.first_name || "",
      last_name: profileData?.last_name || "",
      gender: profileData?.gender || "",
      dob: profileData?.dob
        ? format(parseISO(profileData.dob), "yyyy-MM-dd")
        : "",
      phone: profileData?.phone || "",
      email: profileData?.email || "",
      address: profileData?.address || "",
      photo: profileData?.photo || "",
      signature: profileData?.signature || "",
      resume: profileData?.resume || "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedProfile = await updateProfile(values).unwrap();
        dispatch(setUser({ profile: { ...user?.profile, ...updatedProfile } }));
        toast.success("Profile updated successfully");
        resetForm({ values });
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
    },
  });

  const handleFileChange = async (e) => {
    try {
      setFileLoading({ ...fileLoading, [e.target.name]: true }); // set file uploading status
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadFile(formData).unwrap();
      if (response) {
        setFileLoading({ ...fileLoading, [e.target.name]: false }); //setfile uploading status
        formik.setFieldValue(e.target.name, response.url);
      }
    } catch (error) {
      setFileLoading({ ...fileLoading, [e.target.name]: false }); //setfile uploading status
    }
  };

  return (
    <>
      <Grid container columnSpacing={3} rowSpacing={1}>
        {/* Dialogs */}
        <ChangePasswordDialog
          open={openChangePassword}
          setOpen={setOpenChangePassword}
        />
        <Grid item xs={12} lg={8}>
          <StyledPaper>
            {getProfileLoading && <LinearProgress />}
            <Box className="paperHeader">Edit Profile</Box>
            <Box className="paperBody">
              <form onSubmit={formik.handleSubmit}>
                <Grid container columnSpacing={1} rowSpacing={3}>
                  <Grid item xs={12} lg={2}>
                    <FormControl fullWidth>
                      <Typography variant="formSubHeading">title</Typography>
                      <StyledSelect
                        id="title"
                        name="title"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.title && Boolean(formik.errors.title)
                        }
                      >
                        <MenuItem value="Mr.">Mr</MenuItem>
                        <MenuItem value="Mrs.">Mrs</MenuItem>
                        <MenuItem value="Miss.">Miss</MenuItem>
                        <MenuItem value="Alhaji">Alhaji</MenuItem>
                        <MenuItem value="Hajiya">Hajiya</MenuItem>
                        <MenuItem value="Mallam">Mallam</MenuItem>
                        <MenuItem value="Mallama">Mallama</MenuItem>
                      </StyledSelect>
                      <FormHelperText variant="error">
                        {formik.touched.title && formik.errors.title}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} lg={5}>
                    <FormControl fullWidth>
                      <Typography variant="formSubHeading">
                        first name
                      </Typography>
                      <StyledTextField
                        bold={true}
                        variant="outlined"
                        id="first_name"
                        name="first_name"
                        value={formik.values.first_name}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.first_name &&
                          Boolean(formik.errors.first_name)
                        }
                      />
                      <FormHelperText variant="error">
                        {formik.touched.first_name && formik.errors.first_name}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} lg={5}>
                    <FormControl fullWidth>
                      <Typography variant="formSubHeading">
                        last name
                      </Typography>
                      <StyledTextField
                        bold={true}
                        variant="outlined"
                        id="last_name"
                        name="last_name"
                        value={formik.values.last_name}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.last_name &&
                          Boolean(formik.errors.last_name)
                        }
                      />
                      <FormHelperText variant="error">
                        {formik.touched.last_name && formik.errors.last_name}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  {/* end of first row */}
                  <Grid item xs={12} lg={4}>
                    <FormControl fullWidth>
                      <Typography variant="formSubHeading">Gender</Typography>
                      <StyledSelect
                        bold={true}
                        id="gender"
                        name="gender"
                        value={formik.values.gender}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.gender && Boolean(formik.errors.gender)
                        }
                      >
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                      </StyledSelect>
                      <FormHelperText variant="error">
                        {formik.touched.gender && formik.errors.gender}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <FormControl fullWidth>
                      <Typography variant="formSubHeading">
                        date of birth
                      </Typography>

                      <StyledTextField
                        type="date"
                        id="dob"
                        name="dob"
                        value={formik.values.dob}
                        onChange={formik.handleChange}
                        error={formik.touched.dob && Boolean(formik.errors.dob)}
                      />

                      <FormHelperText variant="error">
                        {formik.touched.dob && formik.errors.dob}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <FormControl fullWidth>
                      <Typography variant="formSubHeading">
                        phone number
                      </Typography>
                      <StyledTextField
                        variant="outlined"
                        id="phone"
                        name="phone"
                        {...PHONE_INPUT_PROPS}
                        value={formik.values.phone}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "phone",
                            formatPhoneNumberInput(e.target.value),
                          )
                        }
                        error={
                          formik.touched.phone && Boolean(formik.errors.phone)
                        }
                      />
                      <FormHelperText variant="error">
                        {formik.touched.phone && formik.errors.phone}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  {/* end of second row */}

                  <Grid item xs={12} lg={4}>
                    <FormControl fullWidth>
                      <Typography variant="formSubHeading">email</Typography>
                      <StyledTextField
                        variant="outlined"
                        id="email"
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.email && Boolean(formik.errors.email)
                        }
                      />
                      <FormHelperText variant="error">
                        {formik.touched.email && formik.errors.email}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} lg={8}>
                    <FormControl fullWidth>
                      <Typography variant="formSubHeading">address</Typography>
                      <StyledTextField
                        variant="outlined"
                        id="address"
                        name="address"
                        value={formik.values.address}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.address &&
                          Boolean(formik.errors.address)
                        }
                      />
                      <FormHelperText variant="error">
                        {formik.touched.address && formik.errors.address}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  {/* end of third row */}

                  <Grid item xs={12} lg={4}>
                    <FormControl fullWidth>
                      <Typography variant="formSubHeading">passport</Typography>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <CustomFileInput
                          id="photo"
                          name="photo"
                          accept="image/*"
                          placeholder="Choose passport photo..."
                          value={formik.values.photo}
                          onChange={(e) => handleFileChange(e)}
                          error={
                            formik.touched.photo && Boolean(formik.errors.photo)
                          }
                        />
                        {fileLoading.photo && <CircularProgress size={20} />}
                      </Stack>

                      <FormHelperText variant="error">
                        {formik.touched.photo && formik.errors.photo}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <FormControl fullWidth>
                      <Typography variant="formSubHeading">
                        signature
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CustomFileInput
                          id="signature"
                          name="signature"
                          accept="image/*"
                          placeholder="Choose signature..."
                          value={formik.values.signature}
                          onChange={(e) => handleFileChange(e)}
                          error={
                            formik.touched.signature &&
                            Boolean(formik.errors.signature)
                          }
                        />
                        {fileLoading.signature && <CircularProgress size={20} />}
                      </Stack>

                      <FormHelperText variant="error">
                        {formik.touched.signature && formik.errors.signature}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <FormControl fullWidth>
                      <Typography variant="formSubHeading">
                        recent resume
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CustomFileInput
                          id="resume"
                          name="resume"
                          accept=".pdf,.doc,.docx"
                          placeholder="Choose resume file..."
                          value={formik.values.resume}
                          onChange={(e) => handleFileChange(e)}
                          error={
                            formik.touched.resume &&
                            Boolean(formik.errors.resume)
                          }
                        />
                        {fileLoading.resume && <CircularProgress size={20} />}
                      </Stack>
                      <FormHelperText variant="error">
                        {formik.touched.resume && formik.errors.resume}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  {/* end of fifth row */}
                  <Grid item xs={12} md={12} textAlign={"right"}>
                    <StyledLoadingButton
                      loading={updateProfileLoading}
                      disabled={!formik.dirty}
                      type="submit"
                    >
                      Update Profile
                    </StyledLoadingButton>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </StyledPaper>
        </Grid>
        <Grid item xs={12} lg={4}>
          <StyledPaper sx={{ minWidth: 0 }}>
            <Box
              className="paperBody"
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
              }}
            >
              <UserAvatar
                src={profileData?.photo}
                name={`${profileData?.first_name || ""} ${profileData?.last_name || ""}`.trim()}
                sx={{ width: 124, height: 124, mb: 2, fontSize: "2.5rem" }}
              />
              <Typography
                variant="formHeading"
                sx={{
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  maxWidth: "100%",
                  textAlign: "center",
                }}
              >
                {`${profileData?.first_name || ""} ${profileData?.last_name || ""}`.trim()}
              </Typography>

              {/* <Typography
                sx={{
                  color: "#333",
                  fontSize: "12px",
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                {userInfo?.first_name + " " + userInfo?.last_name}
              </Typography> */}

              {profileData?.signature && (
                <Box sx={{ mt: 2, width: "100%", maxWidth: 280 }}>
                  <Typography
                    variant="formSubHeading"
                    sx={{ mb: 1, textAlign: "left" }}
                  >
                    Signature
                  </Typography>
                  <Box
                    component="img"
                    src={profileData.signature}
                    alt="uploaded signature"
                    sx={{
                      width: "100%",
                      maxHeight: 100,
                      objectFit: "contain",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1,
                      bgcolor: "#fff",
                    }}
                  />
                </Box>
              )}

              {profileData?.resume && (
                <Button
                  variant="outlined"
                  onClick={() => setOpenCvPreview(true)}
                  sx={{ mt: 2, textTransform: "none" }}
                >
                  Preview Uploaded CV
                </Button>
              )}
            </Box>
          </StyledPaper>
          <StyledPaper>
            <FilledButton fullWidth onClick={() => setOpenChangePassword(true)}>
              Change Password
            </FilledButton>
          </StyledPaper>
        </Grid>
      </Grid>

      <CvPreviewModal
        url={profileData?.resume}
        open={openCvPreview}
        onClose={() => setOpenCvPreview(false)}
      />
    </>
  );
}

export default EditProfile;
